import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { QUERY_STALE_TIME, isSupabaseConfigured } from '@/config';
import { getWcEliminatedTeamIds } from '@/lib/data/provider';

/**
 * The set of World Cup nation ids that are OUT of the tournament (group-stage
 * non-qualifiers + knockout losers). One shared signal every WC surface (album,
 * fantasy, groups, golden boot, bracket) reads to dim/badge eliminated teams.
 *
 * Returns a Set for O(1) membership + an `isEliminated` helper. Refetched on the
 * fixtures cadence since teams drop out as knockout matches finish.
 */
export function useWcEliminatedTeams(): {
  set: Set<string>;
  isEliminated: (teamId: string | null | undefined) => boolean;
  isLoading: boolean;
} {
  const query = useQuery({
    queryKey: ['wc', 'eliminated-teams'],
    queryFn: getWcEliminatedTeamIds,
    staleTime: QUERY_STALE_TIME.fixtures,
    enabled: isSupabaseConfigured,
  });
  const set = useMemo(() => new Set(query.data ?? []), [query.data]);
  const isEliminated = useMemo(
    () => (teamId: string | null | undefined) => (teamId ? set.has(teamId) : false),
    [set],
  );
  return { set, isEliminated, isLoading: query.isPending };
}
